// src/lib/constants/analytics.ts
//
// New Analytics selector vocabularies and query-key factory. Wire selectors are
// STRICT camelCase (no snake aliases) per the gateway contract
// (ref_analytics_gateway_public_contract.md).

export const ANALYTICS_METRICS = ['watchTime', 'sessions', 'uniqueViewers'] as const;
export type AnalyticsMetric = typeof ANALYTICS_METRICS[number];

export const ANALYTICS_METRIC_LABELS: Record<AnalyticsMetric, string> = {
  watchTime: 'Watch time',
  sessions: 'Sessions',
  uniqueViewers: 'Unique viewers',
};

// region unlocked post-release (gateway ADR-0009 / notes adr_014); city joined
// with the per-media geo release (Seq U-5, Go ADR-0011 — served by the
// media-table rollup, same wire shape).
export const ANALYTICS_DIMENSIONS = ['country', 'region', 'city', 'deviceType', 'platform', 'streamType'] as const;
export type AnalyticsDimension = typeof ANALYTICS_DIMENSIONS[number];

// The Audience tab's two-group control (plan §4.4) and the media Drawer's geo
// panel. Geo is also the exact vocabulary of the per-media audience route —
// the technology dimensions are account-grain only and rejected there.
export const ANALYTICS_GEO_DIMENSIONS = ['country', 'region', 'city'] as const;
export type AnalyticsGeoDimension = typeof ANALYTICS_GEO_DIMENSIONS[number];
export const ANALYTICS_TECH_DIMENSIONS = ['deviceType', 'platform', 'streamType'] as const;

export const ANALYTICS_INTERVALS = ['auto', 'minute', 'hour'] as const;
export type AnalyticsInterval = typeof ANALYTICS_INTERVALS[number];

export type AnalyticsScopeKind = 'account' | 'network';

/** Gateway row limits (clamped downstream as well). */
export const TOP_CONTENT_DEFAULT_LIMIT = 20;
export const TOP_CONTENT_MAX_LIMIT = 100;
export const SUBACCOUNTS_DEFAULT_LIMIT = 50;
export const SUBACCOUNTS_MAX_LIMIT = 100;

/** Live data polls exactly once per 60 s (phase-plan bound; never faster). */
export const ANALYTICS_LIVE_POLL_INTERVAL_MS = 60_000;

/** A live snapshot older than this is flagged stale in the UI (~3 min). */
export const ANALYTICS_LIVE_STALE_AFTER_SECONDS = 180;

/**
 * Hierarchical TanStack Query keys (ADR-003 §6). Every scoped key embeds the
 * literal 'byCustomer' (consumed by AuthProvider.setSelectedCId's invalidation
 * predicate) plus the scope kind and cid, so keys can never collide across
 * customer selections. Account-only views pin kind to 'account'; the network
 * leaderboard pins 'network'.
 */
export const ANALYTICS_QUERY_KEYS = {
  all: ['analytics'] as const,
  // Global, not per-customer: /meta is account-independent gateway metadata.
  meta: () => [...ANALYTICS_QUERY_KEYS.all, 'meta'] as const,
  scoped: (kind: AnalyticsScopeKind, cid: number) =>
    [...ANALYTICS_QUERY_KEYS.all, 'byCustomer', kind, cid] as const,
  overview: (kind: AnalyticsScopeKind, cid: number, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'overview', from, to] as const,
  playbackDaily: (kind: AnalyticsScopeKind, cid: number, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'playbackDaily', from, to] as const,
  topContent: (
    kind: AnalyticsScopeKind,
    cid: number,
    from: number,
    to: number,
    metric: AnalyticsMetric,
    limit: number
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'topContent', from, to, metric, limit] as const,
  concurrency: (
    kind: AnalyticsScopeKind,
    cid: number,
    from: number,
    to: number,
    interval: AnalyticsInterval
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'concurrency', from, to, interval] as const,
  live: (kind: AnalyticsScopeKind, cid: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'live'] as const,
  liveByMedia: (cid: number, limit: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'liveByMedia', limit] as const,
  audience: (
    cid: number,
    from: number,
    to: number,
    dimension: AnalyticsDimension,
    metric: AnalyticsMetric
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'audience', dimension, metric, from, to] as const,
  // mediaId stays the wire STRING (never a Number) — inside query keys too.
  mediaAudience: (
    cid: number,
    mediaId: string,
    from: number,
    to: number,
    dimension: AnalyticsGeoDimension,
    metric: AnalyticsMetric
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaAudience', mediaId, dimension, metric, from, to] as const,
  heatmap: (cid: number, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'heatmap', from, to] as const,
  transfer: (
    kind: AnalyticsScopeKind,
    cid: number,
    from: number,
    to: number,
    finalizedOnly: boolean
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'transfer', from, to, finalizedOnly] as const,
  subaccounts: (
    cid: number,
    from: number,
    to: number,
    metric: AnalyticsMetric,
    limit: number
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped('network', cid), 'subaccounts', from, to, metric, limit] as const,
} as const;
